import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Trash2,
  Edit2,
  Key,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  X,
  Shield,
  MessageSquare,
  Settings,
  Mail,
  Check,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";

const ROLE_ICONS = {
  super_admin: Shield,
  quotes_manager: MessageSquare,
  site_config_manager: Settings,
  email_config_manager: Mail,
  user_manager: Users,
};

const ROLE_COLORS = {
  super_admin: "text-[#DC2626] bg-[#DC2626]/10 border-[#DC2626]/20",
  quotes_manager: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  site_config_manager: "text-purple-400 bg-purple-400/10 border-purple-400/20",
  email_config_manager: "text-green-400 bg-green-400/10 border-green-400/20",
  user_manager: "text-orange-400 bg-orange-400/10 border-orange-400/20",
};

export default function UserManagementPage() {
  const { authAxios, admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({ username: "", password: "", roles: ["quotes_manager"] });
  const [passwordData, setPasswordData] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        authAxios.get("/users"),
        authAxios.get("/users/roles"),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setMessage({ type: "error", text: "You don't have permission to manage users" });
      } else {
        setMessage({ type: "error", text: "Failed to load users" });
      }
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const toggleRole = (roleId) => {
    setFormData(prev => {
      const currentRoles = prev.roles || [];
      if (currentRoles.includes(roleId)) {
        // Remove role (but keep at least one)
        const newRoles = currentRoles.filter(r => r !== roleId);
        return { ...prev, roles: newRoles.length > 0 ? newRoles : currentRoles };
      } else {
        // Add role
        return { ...prev, roles: [...currentRoles, roleId] };
      }
    });
  };

  const handleCreateUser = async () => {
    if (!formData.username || !formData.password) {
      showMsg("error", "Username and password are required");
      return;
    }
    if (!formData.roles || formData.roles.length === 0) {
      showMsg("error", "At least one role is required");
      return;
    }
    setSubmitting(true);
    try {
      await authAxios.post("/users", formData);
      showMsg("success", `User '${formData.username}' created successfully`);
      setShowCreateModal(false);
      setFormData({ username: "", password: "", roles: ["quotes_manager"] });
      fetchData();
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async () => {
    if (!formData.username) {
      showMsg("error", "Username is required");
      return;
    }
    if (!formData.roles || formData.roles.length === 0) {
      showMsg("error", "At least one role is required");
      return;
    }
    setSubmitting(true);
    try {
      await authAxios.put(`/users/${selectedUser.id}`, {
        username: formData.username,
        roles: formData.roles,
      });
      showMsg("success", "User updated successfully");
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.new_password) {
      showMsg("error", "New password is required");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      showMsg("error", "Passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 6) {
      showMsg("error", "Password must be at least 6 characters");
      return;
    }
    
    const isSelf = selectedUser.username === admin?.username;
    if (isSelf && !passwordData.current_password) {
      showMsg("error", "Current password is required to change your own password");
      return;
    }
    
    setSubmitting(true);
    try {
      await authAxios.put(`/users/${selectedUser.id}/password`, {
        current_password: isSelf ? passwordData.current_password : undefined,
        new_password: passwordData.new_password,
      });
      showMsg("success", "Password changed successfully");
      setShowPasswordModal(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    setSubmitting(true);
    try {
      await authAxios.delete(`/users/${selectedUser.id}`);
      showMsg("success", `User '${selectedUser.username}' deleted`);
      setShowDeleteModal(false);
      fetchData();
    } catch (err) {
      showMsg("error", err.response?.data?.detail || "Failed to delete user");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({ username: user.username, roles: user.roles || [] });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    setShowPasswordModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const getRoleNames = (userRoles) => {
    if (!userRoles || userRoles.length === 0) return "No roles";
    return userRoles.map(roleId => {
      const role = roles.find(r => r.id === roleId);
      return role?.name || roleId;
    }).join(", ");
  };

  const getAggregatedPermissions = (userRoles) => {
    if (!userRoles) return [];
    const perms = new Set();
    userRoles.forEach(roleId => {
      const role = roles.find(r => r.id === roleId);
      if (role?.permissions) {
        role.permissions.forEach(p => perms.add(p));
      }
    });
    return Array.from(perms);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-gray-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading users...
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">User Management</h1>
          <p className="text-gray-500 text-sm">Manage admin users and their roles</p>
        </div>
        <div className="flex items-center gap-4">
          {message && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm ${
              message.type === "success" ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"
            }`}>
              {message.type === "success" ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              {message.text}
            </div>
          )}
          <Button
            onClick={() => {
              setFormData({ username: "", password: "", roles: ["quotes_manager"] });
              setShowCreateModal(true);
            }}
            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-[#111118] border border-white/[0.04] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/[0.04]">
              <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-6 py-4">User</th>
              <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-6 py-4">Roles</th>
              <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-6 py-4">Permissions</th>
              <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const userRoles = user.roles || [];
              const primaryRole = userRoles[0] || "quotes_manager";
              const RoleIcon = ROLE_ICONS[primaryRole] || Users;
              const roleColor = ROLE_COLORS[primaryRole] || "text-gray-400 bg-gray-400/10";
              const isSelf = user.username === admin?.username;
              const permissions = getAggregatedPermissions(userRoles);
              
              return (
                <tr key={user.id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02]">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${roleColor}`}>
                        <RoleIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">
                          {user.username}
                          {isSelf && <span className="text-gray-500 text-xs ml-2">(you)</span>}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1.5">
                      {userRoles.map((roleId) => {
                        const role = roles.find(r => r.id === roleId);
                        const color = ROLE_COLORS[roleId] || "text-gray-400 bg-gray-400/10";
                        return (
                          <span key={roleId} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>
                            {role?.name || roleId}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {permissions.map((perm) => (
                        <span key={perm} className="text-gray-500 text-xs bg-white/[0.04] px-2 py-0.5 rounded">
                          {perm.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPasswordModal(user)}
                        className="text-gray-400 hover:text-white hover:bg-white/[0.04] h-8 px-2"
                        title="Change Password"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(user)}
                        className="text-gray-400 hover:text-white hover:bg-white/[0.04] h-8 px-2"
                        title="Edit User"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {!isSelf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteModal(user)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
                          title="Delete User"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#111118] border-white/[0.04] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username"
                className="bg-[#0A0A0F] border-white/[0.06] text-white"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  className="bg-[#0A0A0F] border-white/[0.06] text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Roles (select one or more)</Label>
              <div className="space-y-2 bg-[#0A0A0F] border border-white/[0.06] rounded-lg p-3">
                {roles.filter(r => r.id !== "super_admin").map((role) => {
                  const RoleIcon = ROLE_ICONS[role.id] || Users;
                  const isSelected = formData.roles?.includes(role.id);
                  return (
                    <div
                      key={role.id}
                      onClick={() => toggleRole(role.id)}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                        isSelected ? "bg-[#DC2626] border-[#DC2626]" : "border-white/20"
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <RoleIcon className="h-4 w-4 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-white text-sm">{role.name}</p>
                        <p className="text-gray-500 text-xs">{role.permissions.join(", ")}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-white/10 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={submitting} className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#111118] border-white/[0.04] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-[#0A0A0F] border-white/[0.06] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Roles (select one or more)</Label>
              <div className="space-y-2 bg-[#0A0A0F] border border-white/[0.06] rounded-lg p-3">
                {roles.map((role) => {
                  const RoleIcon = ROLE_ICONS[role.id] || Users;
                  const isSelected = formData.roles?.includes(role.id);
                  return (
                    <div
                      key={role.id}
                      onClick={() => toggleRole(role.id)}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                        isSelected ? "bg-[#DC2626] border-[#DC2626]" : "border-white/20"
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <RoleIcon className="h-4 w-4 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-white text-sm">{role.name}</p>
                        <p className="text-gray-500 text-xs">{role.permissions.join(", ")}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-white/10 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleEditUser} disabled={submitting} className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="bg-[#111118] border-white/[0.04] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Change Password {selectedUser && `for ${selectedUser.username}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedUser?.username === admin?.username && (
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Current Password</Label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  placeholder="Enter current password"
                  className="bg-[#0A0A0F] border-white/[0.06] text-white"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">New Password</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Enter new password"
                className="bg-[#0A0A0F] border-white/[0.06] text-white"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-400 text-xs">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Confirm new password"
                className="bg-[#0A0A0F] border-white/[0.06] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)} className="border-white/10 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={submitting} className="bg-[#DC2626] hover:bg-[#B91C1C]">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#111118] border-white/[0.04] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-gray-400 py-4">
            Are you sure you want to delete <span className="text-white font-medium">{selectedUser?.username}</span>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-white/10 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
